import { Package } from "@phosphor-icons/react"

import { InlineAlert, RadioGroup, RadioGroupItem } from "~/components/design-system/components"
import { pluralize } from "~/utils/formatting"
import { PackageFolderPage } from "./PackageFolderPage"
import { PackageFolderTree } from "./PackageFolderTree"
import { packagesInFolder } from "./packageFolders"
import {
  PackageNavigatorProvider,
  usePackageNavigator,
  type PendingFolderMove,
  type PendingPackageMove,
} from "./packageNavigator"
import PackageWizard from "./PackageWizard"
import { PackagePage } from "./PackagePage"
import { PackageStoresCommandbar } from "./setup/setupDrafts"
import { useAllocationPackageFolders, useAllocationPackages } from "./useAllocationData"
import type { PackageFolder } from "./types"

// ─────────────────────────────────────────────────────────────────────────────
// RangeTab (default export)
// ─────────────────────────────────────────────────────────────────────────────

function MovePackageConfirmation({
  move,
  saving,
  stagedReason,
  onChoose,
  onCancel,
  onApply,
}: {
  move: PendingPackageMove
  saving: boolean
  /** Why it can't take the folder's stores yet. */
  stagedReason: string | undefined
  onChoose: (stores: PendingPackageMove["stores"]) => void
  onCancel: () => void
  onApply: () => void
}) {
  const { pkg, folder } = move
  return (
    <InlineAlert
      tone="info"
      size="block"
      title={`Move “${pkg.name}” to ${folder.name}?`}
      description={
        <div className="flex flex-col gap-2">
          <span>{pluralize(pkg.productIds.length, "variant")} move with it. Stores:</span>
          <RadioGroup
            value={move.stores}
            onValueChange={(value) => {
              if (value === "keep" || value === "follow") onChoose(value)
            }}
          >
            <label className="flex items-start gap-2">
              <RadioGroupItem value="keep" className="mt-0.5" />
              <span className="label-base text-fg-base">
                Keep its store assignment ({pluralize(pkg.storeIds.length, "store")})
              </span>
            </label>
            <label className="flex items-start gap-2">
              <RadioGroupItem
                value="follow"
                className="mt-0.5"
                disabled={stagedReason !== undefined}
              />
              <span className="flex flex-col">
                <span className="label-base text-fg-base">
                  {`Follow ${folder.name}’s stores (${pluralize(folder.storeIds.length, "store")})`}
                </span>
                {stagedReason && <span className="paragraph-sm text-fg-muted">{stagedReason}</span>}
              </span>
            </label>
          </RadioGroup>
          <span className="paragraph-sm text-fg-muted">
            The package keeps its own dates, variants, SKUs and hand-set variant dates.
          </span>
        </div>
      }
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
      action={{ label: "Move", onClick: onApply, requires: "edit", disabled: saving }}
    />
  )
}

function folderMoveNote(folder: PackageFolder, parent: PackageFolder, packageCount: number) {
  if (packageCount === 0) return `${folder.name} is empty. It keeps its own dates and stores.`
  const moves = `${pluralize(packageCount, "package")} move with it`
  if (folder.ownStartDate === null && parent.startDate !== null) {
    return `${moves} and keep their stores. ${folder.name} has no dates, so packages without dates of their own will use ${parent.name}’s.`
  }
  return `${moves} and keep their dates and stores.`
}

/** A folder landing on a folder becomes its subfolder; packages that followed its dates follow the new parent's. */
function MoveFolderConfirmation({
  move,
  packageCount,
  saving,
  onCancel,
  onApply,
}: {
  move: PendingFolderMove
  packageCount: number
  saving: boolean
  onCancel: () => void
  onApply: () => void
}) {
  const { folder, parent } = move
  return (
    <InlineAlert
      tone="info"
      size="block"
      title={`Move “${folder.name}” into ${parent.name} as a subfolder?`}
      description={folderMoveNote(folder, parent, packageCount)}
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
      action={{ label: "Move", onClick: onApply, requires: "edit", disabled: saving }}
    />
  )
}

function RangeTabBody() {
  const nav = usePackageNavigator()
  const draft = nav.draft
  const { packages, folders, selection, pendingMove, pendingFolderMove } = nav
  const selectedPkg =
    selection?.kind === "package" ? packages.find((p) => p.id === selection.id) : undefined
  const selectedFolder =
    selection?.kind === "folder" ? folders.find((f) => f.id === selection.id) : undefined
  return (
    <div className="flex flex-1 overflow-hidden">
      <PackageFolderTree />
      <div className="flex flex-1 flex-col overflow-hidden">
        {pendingMove && (
          <div className="px-6 pt-6">
            <MovePackageConfirmation
              move={pendingMove}
              saving={nav.busy}
              stagedReason={draft.stagedStoresReason([pendingMove.pkg.id])}
              onChoose={nav.chooseMoveStores}
              onCancel={nav.cancelMove}
              onApply={nav.confirmMove}
            />
          </div>
        )}
        {pendingFolderMove && (
          <div className="px-6 pt-6">
            <MoveFolderConfirmation
              move={pendingFolderMove}
              packageCount={packagesInFolder(packages, folders, pendingFolderMove.folder.id).length}
              saving={nav.busy}
              onCancel={nav.cancelFolderMove}
              onApply={nav.confirmFolderMove}
            />
          </div>
        )}
        {selectedPkg && <PackagePage key={selectedPkg.id} pkg={selectedPkg} />}
        {selectedFolder && <PackageFolderPage key={selectedFolder.id} folder={selectedFolder} />}
        {!selectedPkg && !selectedFolder && (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <Package size={40} className="mx-auto text-fg-subtle" />
              <p className="mt-2 text-sm text-fg-subtle">
                {packages.length + folders.length === 0
                  ? "No packages yet"
                  : "Select a folder or package to view details"}
              </p>
            </div>
          </div>
        )}
      </div>
      {nav.wizardMode && (
        <PackageWizard
          mode={nav.wizardMode}
          onClose={nav.closeWizard}
          onCreated={(packageId) => nav.select({ kind: "package", id: packageId })}
        />
      )}
    </div>
  )
}

export default function RangeTab() {
  const { packages, isLoading } = useAllocationPackages()
  const { folders, isLoading: foldersLoading } = useAllocationPackageFolders()
  return (
    <div className="flex h-full flex-col">
      {isLoading || foldersLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-fg-subtle">Loading packages...</p>
        </div>
      ) : (
        <PackageNavigatorProvider packages={packages} folders={folders}>
          <RangeTabBody />
        </PackageNavigatorProvider>
      )}
      <PackageStoresCommandbar />
    </div>
  )
}
